import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { useToast } from '../lib/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const AliasModal = ({ isOpen, onClose, redirectOnSuccess = true }) => {
  const [alias, setAlias] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [isAvailable, setIsAvailable] = useState(true);
  const { updateAlias, isAliasAvailable } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Vérifier la disponibilité de l'alias en temps réel
  useEffect(() => {
    const checkAlias = async () => {
      if (alias.length < 3) {
        setIsAvailable(true);
        return;
      }

      setIsChecking(true);
      try {
        const available = await isAliasAvailable(alias);
        setIsAvailable(available);
      } catch (error) {
        console.error("Erreur lors de la vérification de l'alias:", error);
        setIsAvailable(false);
      } finally {
        setIsChecking(false);
      }
    };

    const timeoutId = setTimeout(checkAlias, 500);
    return () => clearTimeout(timeoutId);
  }, [alias, isAliasAvailable]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!alias || alias.length < 3) {
      toast({
        title: "Erreur",
        description: "L'alias doit contenir au moins 3 caractères.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      await updateAlias(alias);
      toast({
        title: "Succès",
        description: "Votre alias a été mis à jour avec succès.",
        variant: "success",
      });
      onClose(alias);
      if (redirectOnSuccess) {
        navigate(`/project/${alias}`);
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.message || "Une erreur est survenue lors de la mise à jour de l'alias.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Choisissez votre alias</DialogTitle>
          <DialogDescription>
            Votre alias sera utilisé pour votre URL personnelle et doit être unique.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="alias">Alias</Label>
            <Input
              id="alias"
              value={alias}
              onChange={(e) => setAlias(e.target.value)}
              placeholder="votre-alias"
              className={!isAvailable ? "border-red-500" : ""}
              autoComplete="off"
            />
            {isChecking && (
              <p className="text-sm text-muted-foreground">Vérification de la disponibilité...</p>
            )}
            {!isChecking && !isAvailable && (
              <p className="text-sm text-red-500">Cet alias est déjà utilisé</p>
            )}
            {!isChecking && isAvailable && alias.length >= 3 && (
              <p className="text-sm text-green-500">Cet alias est disponible</p>
            )}
          </div>
          <Button
            type="submit"
            className="w-full"
            disabled={isLoading || !isAvailable || alias.length < 3}
          >
            {isLoading ? "Mise à jour..." : "Confirmer"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AliasModal; 